import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4nbnk1hk4g2rg9v08c0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/2b05cb83-296d-440e-81cd-748e597a30be.png" alt="MIRZA Logo" className="h-10 w-10 object-contain" />
              <span className={`text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>MIRZA</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white hover:text-purple-300'}`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white hover:text-purple-300'}`}>About</button>
              <button onClick={() => scrollToSection('features')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white hover:text-purple-300'}`}>Features</button>
              <button onClick={() => scrollToSection('pricing')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white hover:text-purple-300'}`}>Pricing</button>
              <button onClick={() => scrollToSection('support')} className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-purple-600' : 'text-white hover:text-purple-300'}`}>Support</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden cursor-pointer">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap">Features</button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap">Pricing</button>
              <button onClick={() => scrollToSection('support')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-50 cursor-pointer whitespace-nowrap">Support</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-700 to-cyan-500"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-purple-300/50 bg-white/10 backdrop-blur-sm mb-8">
                <i className="ri-shield-check-line text-lg"></i>
                <span className="text-sm font-medium whitespace-nowrap">Enterprise-Grade VPN Security</span>
              </div>
              
              <h1 className="text-6xl md:text-7xl font-extrabold mb-10 leading-tight">Secure Your Digital Freedom</h1>
              
              <p className="text-xl text-white/80 mb-12 leading-relaxed">Military-grade encryption protecting your online privacy across all devices and networks</p>
              
              <div className="flex flex-wrap gap-4">
                <button onClick={() => scrollToSection('pricing')} className="px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  Get Started Now
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button onClick={() => scrollToSection('features')} className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  Learn More
                </button>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative hidden md:block">
              <div className="relative z-10 animate-float">
                <img src="https://public.readdy.ai/ai/img_res/4304863f9e952e96065dd3eee0272693.jpg" alt="VPN Security" className="w-full h-auto object-contain drop-shadow-2xl" />
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold mb-14">
                // WHO WE ARE
              </div>
              
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">Protecting Privacy Since Day One</h2>
              
              <div className="space-y-10">
                <p className="text-lg text-gray-600 leading-relaxed">
                  At MIRZA, we believe that online privacy is a fundamental right, not a luxury. Our mission is to provide enterprise-grade VPN security that's accessible to everyone, ensuring your digital freedom remains protected at all times.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  With cutting-edge encryption technology and a global network of secure servers, we deliver the highest standards of digital security. Our team of cybersecurity experts works tirelessly to stay ahead of emerging threats and vulnerabilities.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  We put our users first in everything we do. From our strict no-log policy to our 24/7 customer support, every decision is made with your privacy and satisfaction in mind. Your trust is our most valuable asset.
                </p>
              </div>

              <div className="mt-10 pl-6 border-l-4 border-purple-600 space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <i className="ri-mail-line text-purple-600"></i>
                  <a href="mailto:support@mirzaweb.com" className="hover:text-purple-600 transition-colors cursor-pointer">support@mirzaweb.com</a>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <i className="ri-phone-line text-purple-600"></i>
                  <a href="tel:+12673148110" className="hover:text-purple-600 transition-colors cursor-pointer">+1 267 314 8110</a>
                </div>
                <div className="flex items-start gap-3 text-gray-700">
                  <i className="ri-map-pin-line text-purple-600 mt-1"></i>
                  <span>68 RUE DES ALLIES, 42100 SAINT-ETIENNE, France</span>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Modern%20cybersecurity%20operations%20center%20with%20abstract%20digital%20security%20visualization%2C%20purple%20and%20cyan%20color%20grading%2C%20professional%20tech%20environment%20with%20holographic%20network%20displays%20and%20encryption%20symbols%2C%20sleek%20minimalist%20design%2C%20futuristic%20security%20concept%20with%20geometric%20patterns%20and%20glowing%20elements%2C%20high-tech%20atmosphere&width=700&height=800&seq=about-security-viz&orientation=portrait" alt="MIRZA Security" className="w-full h-full object-cover" />
              </div>
              
              <div className="absolute bottom-8 right-8 bg-white rounded-2xl shadow-xl p-6 border-l-4 border-purple-600">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                    <i className="ri-shield-check-fill text-3xl text-purple-600"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">AES-256</div>
                    <div className="text-sm text-gray-600 whitespace-nowrap">Military-Grade Encryption</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-purple-600 text-white rounded-full text-sm font-semibold mb-6">
              // FEATURES
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose MIRZA VPN</h2>
            <p className="text-xl text-gray-600">Advanced security features designed for modern digital life</p>
          </div>

          {/* Feature Grid */}
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            {/* Large Feature Card */}
            <div className="md:col-span-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-12 text-white flex flex-col justify-between min-h-[500px]">
              <div className="flex-1 flex items-center justify-center">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-shield-check-line text-8xl animate-pulse"></i>
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-4">Military-Grade Encryption</h3>
                <p className="text-white/80 text-lg mb-6 leading-relaxed">
                  AES-256 encryption ensures your data remains completely secure and private, protecting you from hackers, ISPs, and government surveillance.
                </p>
                <a href="#" className="inline-flex items-center gap-2 text-white font-semibold hover:gap-3 transition-all cursor-pointer">
                  Learn More <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* Right Column Features */}
            <div className="md:col-span-3 grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-global-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Global Server Network</h3>
                <p className="text-gray-600 leading-relaxed">
                  Access content from anywhere with our worldwide network of high-speed servers strategically located across the globe.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-flashlight-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Ultra-Fast Connection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Experience blazing-fast speeds with zero lag. Stream, download, and browse without compromising on performance.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-eye-off-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No-Log Policy</h3>
                <p className="text-gray-600 leading-relaxed">
                  We never track, collect, or store your online activity. Your privacy is guaranteed with our strict no-log policy.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-shut-down-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Kill Switch Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automatic kill switch instantly disconnects your internet if VPN connection drops, ensuring your data never leaks.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Features Row */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-center gap-4 hover:border-purple-600 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-file-shield-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">DNS Leak Protection</h4>
                <p className="text-sm text-gray-600">Prevent DNS leaks automatically</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-center gap-4 hover:border-purple-600 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-device-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Multi-Device Support</h4>
                <p className="text-sm text-gray-600">Connect up to 5 devices simultaneously</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 flex items-center gap-4 hover:border-purple-600 transition-all cursor-pointer">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">24/7 Support</h4>
                <p className="text-sm text-gray-600">Always here when you need us</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 bg-gradient-to-br from-purple-900 via-purple-700 to-indigo-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold mb-8">
              // PRICING
            </div>
            <h2 className="text-6xl font-bold text-white mb-5">Simple, Transparent Pricing</h2>
            <p className="text-xl text-white/70">One plan, unlimited protection</p>
          </div>

          {/* Pricing Card */}
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-3xl p-16 shadow-2xl">
              <div className="text-center mb-8">
                <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-bold mb-6">
                  MOST POPULAR
                </div>
                <div className="flex items-baseline justify-center gap-2 mb-8">
                  <span className="text-7xl font-bold text-gray-900">$9.99</span>
                  <span className="text-2xl text-gray-500">/month</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8 mb-8"></div>

              <ul className="space-y-5 mb-12">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-700">Unlimited bandwidth and speed</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-700">Access to all global servers</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-700">Connect up to 5 devices simultaneously</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-700">24/7 customer support</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-purple-600 flex-shrink-0"></i>
                  <span className="text-lg text-gray-700">30-day money-back guarantee</span>
                </li>
              </ul>

              <button className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all whitespace-nowrap cursor-pointer">
                Get Started Now
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">No credit card required for trial</p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 bg-white border-t-2 border-purple-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left Column */}
            <div>
              <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold mb-7">
                // SUPPORT
              </div>
              
              <h2 className="text-5xl font-bold text-gray-900 mb-6">We're Here to Help</h2>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-12">
                Our dedicated support team is available around the clock to assist you with any questions or concerns. We're committed to providing you with the best possible experience.
              </p>

              <div className="space-y-5">
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Email Support</h3>
                      <a href="mailto:support@mirzaweb.com" className="text-purple-600 font-semibold mb-1 block hover:text-purple-700 cursor-pointer">support@mirzaweb.com</a>
                      <p className="text-sm text-gray-600">Response within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Phone Support</h3>
                      <a href="tel:+12673148110" className="text-purple-600 font-semibold mb-1 block hover:text-purple-700 cursor-pointer">+1 267 314 8110</a>
                      <p className="text-sm text-gray-600">Mon-Fri, 9AM-6PM CET</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Office Address</h3>
                      <p className="text-gray-700 font-semibold mb-1">68 RUE DES ALLIES</p>
                      <p className="text-sm text-gray-600">42100 SAINT-ETIENNE, France</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div>
              <div className="bg-white rounded-2xl border-2 border-gray-100 p-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h3>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all resize-none"
                      placeholder="Your message (max 500 characters)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-center">
                      Message sent successfully! We'll get back to you soon.
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
                      Failed to send message. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 rounded-t-[40px] relative overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute bottom-0 left-0 right-0 text-center opacity-5 pointer-events-none">
          <span className="text-[200px] font-bold text-white">MIRZA</span>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Column 1: Brand */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/2b05cb83-296d-440e-81cd-748e597a30be.png" alt="MIRZA Logo" className="h-10 w-10 object-contain" />
                <span className="text-3xl font-bold text-white">MIRZA</span>
              </div>
              <p className="text-gray-400 mb-8">Your Privacy, Our Priority</p>
              <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-download-line"></i>
                Download Now
              </button>
              <div className="flex items-center gap-4 mt-6">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all cursor-pointer">
                  <i className="ri-twitter-x-line text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-white"></i>
                </a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className="text-xl font-bold text-white mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Pricing</button></li>
                <li><button onClick={() => scrollToSection('support')} className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Support</button></li>
              </ul>
            </div>

            {/* Column 3: Resources */}
            <div>
              <h4 className="text-xl font-bold text-white mb-6">Resources</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">FAQ</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-purple-400 hover:translate-x-1 transition-all inline-block cursor-pointer">Blog</a></li>
              </ul>
            </div>

            {/* Column 4: Newsletter */}
            <div>
              <h4 className="text-xl font-bold text-white mb-4">Stay Updated</h4>
              <p className="text-gray-400 text-sm mb-6">Get security tips and updates</p>
              <form id="newsletter-form" data-readdy-form action="https://readdy.ai/api/form/d4nbnk1hk4g2rg9v08bg" method="POST" className="flex mb-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-3 bg-gray-800 text-white rounded-l-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-sm"
                />
                <button type="submit" className="px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-r-lg hover:from-purple-700 hover:to-indigo-700 transition-all cursor-pointer">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
              <p className="text-gray-500 text-xs">We respect your privacy</p>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2025 MIRZA. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-purple-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
